import { useCallback, useMemo, type ReactNode } from 'react';
import { create } from 'zustand';

const DEFAULT_NOTIFICATION_DURATION = 5000; // 5 seconds
const DEFAULT_NOTIFICATION_PERSISTENCE = false;

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: ReactNode;
  duration?: number;
  persistent?: boolean;
  action?: ReactNode;
}

interface NotificationState {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],

  addNotification: (notification) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    const newNotification: Notification = {
      id,
      ...notification,
      duration: notification.duration ?? DEFAULT_NOTIFICATION_DURATION,
      persistent: notification.persistent ?? DEFAULT_NOTIFICATION_PERSISTENCE,
    };

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove after duration (unless persistent)
    if (!newNotification.persistent && newNotification.duration) {
      setTimeout(() => {
        get().removeNotification(id);
      }, newNotification.duration);
    }
  },

  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearAll: () => {
    set({ notifications: [] });
  },
}));

// Convenience hooks
export const useNotifications = () => {
  const addNotification = useNotificationStore((state) => state.addNotification);

  interface NotificationOptions {
    duration?: number;
    persistent?: boolean;
    action?: ReactNode;
  }

  const success = useCallback(
    (title: string, message?: ReactNode, options?: NotificationOptions) =>
      addNotification({ type: 'success', title, message, ...options }),
    [addNotification],
  );

  const error = useCallback(
    (title: string, message?: ReactNode, options?: NotificationOptions) =>
      addNotification({
        type: 'error',
        title,
        message,
        ...options,
      }),
    [addNotification],
  );

  const warning = useCallback(
    (title: string, message?: ReactNode, options?: NotificationOptions) =>
      addNotification({ type: 'warning', title, message, ...options }),
    [addNotification],
  );

  const info = useCallback(
    (title: string, message?: ReactNode, options?: NotificationOptions) =>
      addNotification({ type: 'info', title, message, ...options }),
    [addNotification],
  );

  return useMemo(() => ({ success, error, warning, info }), [success, error, warning, info]);
};
